import { useState } from 'react';

/**
 * Per-device ordering of the patient Summary's main content blocks. Edited on
 * the Profile page (kept off the Summary itself so that page stays uncluttered)
 * and saved like the other display prefs.
 *
 * The clinician note is intentionally NOT a block here — it stays pinned at the
 * bottom of the feed. The date toolbar and (optional) KPI row stay pinned at
 * the top.
 */
export type SummaryBlockKey =
  | 'food-quality'
  | 'nutrient-distribution'
  | 'glucose'
  | 'meal-timing';

/** Blocks in their default order, with i18n labels for the Profile editor. */
export const SUMMARY_BLOCKS: Array<{ key: SummaryBlockKey; label: string }> = [
  { key: 'food-quality', label: 'Average Plate & Mediterranean Score' },
  { key: 'nutrient-distribution', label: 'Nutrient timing & Macronutrient distribution' },
  { key: 'glucose', label: 'Glucose' },
  { key: 'meal-timing', label: 'Meal timing' },
];

const STORAGE_KEY = 'ui.summaryLayout';
/** The blocks in their canonical default order. */
export const SUMMARY_LAYOUT_DEFAULT: SummaryBlockKey[] = SUMMARY_BLOCKS.map((b) => b.key);
const DEFAULT_ORDER = SUMMARY_LAYOUT_DEFAULT;
const VALID_KEYS = new Set<SummaryBlockKey>(DEFAULT_ORDER);

/** True when `order` matches the default order exactly. */
export const isDefaultSummaryLayout = (order: SummaryBlockKey[]): boolean =>
  order.length === DEFAULT_ORDER.length && order.every((k, i) => k === DEFAULT_ORDER[i]);

/**
 * Keep only known keys (deduped) and append any blocks missing from the saved
 * order in their default position — so introducing a new block later doesn't
 * hide it for clinicians with an older saved order.
 */
const sanitize = (order: unknown): SummaryBlockKey[] => {
  const seen = new Set<SummaryBlockKey>();
  const out: SummaryBlockKey[] = [];
  if (Array.isArray(order)) {
    for (const k of order) {
      if (VALID_KEYS.has(k as SummaryBlockKey) && !seen.has(k as SummaryBlockKey)) {
        seen.add(k as SummaryBlockKey);
        out.push(k as SummaryBlockKey);
      }
    }
  }
  for (const k of DEFAULT_ORDER) if (!seen.has(k)) out.push(k);
  return out;
};

export const readSummaryLayout = (): SummaryBlockKey[] => {
  if (typeof window === 'undefined') return DEFAULT_ORDER;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? sanitize(JSON.parse(raw)) : DEFAULT_ORDER;
  } catch {
    return DEFAULT_ORDER;
  }
};

export const useSummaryLayout = (): [SummaryBlockKey[], (next: SummaryBlockKey[]) => void] => {
  const [value, setValue] = useState<SummaryBlockKey[]>(readSummaryLayout);
  const set = (next: SummaryBlockKey[]) => {
    const cleaned = sanitize(next);
    setValue(cleaned);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned));
    } catch {
      /* private mode / quota — ignore */
    }
  };
  return [value, set];
};
